import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/ui/wards/common/button/Button';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import ProgressSearchCell from './ProgressCheckSearchResults';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { useNavigate } from 'react-router-dom';
import { useWaSummaryStore } from 'src/api/wa/store/WaSummaryStore';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import DropdownSelect, { OptionProps } from 'src/ui/wards/common/select/DropdownSelect';
import { FormRow } from 'src/ui/wards/common/form/FormRow';
import { defaultMapCenter } from 'src/common/location/defaultMapSettings';

export default function S6_1_ProgressCheck() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    fetchWorkplaceMobileSummary,
    fetchBranchOptions,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
  } = useWaWorkplaceService();
  const { summary, setSummary } = useWaSummaryStore();
  const {
    branchOptions,
    salesOfficeOptions,
    sectionOptions,
    workplaceFilterParameters,
    patchWorkplaceFilterParameters,
    setSalesOfficeOptions,
    setSectionOptions,
    setMapCenter,
    setUserOfficeLocation,
    mapCenterUserLocation,
  } = useWaFilterStore();
  const { user } = useWaUserStore();
  const [showMissingBranchOffice, setShowMissingBranchOffice] = useState(false);
  const [showMissingLocalOffice, setShowMissingLocalOffice] = useState(false);
  const [showMissingSection, setShowMissingSection] = useState(false);

  useEffect(() => {
    setSummary(null);
    const { branchCode, salesOfficeCode, sectionCode } = workplaceFilterParameters;
    if (branchCode && salesOfficeCode && sectionCode) {
      fetchWorkplaceMobileSummary({ branchCode, salesOfficeCode, sectionCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBranchOptions();
  }, [fetchBranchOptions]);

  useEffect(() => {
    const salesOfficeCode = workplaceFilterParameters.salesOfficeCode;
    const sectionCode = workplaceFilterParameters.sectionCode;
    const salesOffice = salesOfficeOptions.find((office) => office.code === salesOfficeCode);
    const section = sectionOptions.find((sec) => sec.code === sectionCode);
    const officeLocation = section?.location ?? salesOffice?.location ?? null;
    setUserOfficeLocation(officeLocation);

    if (mapCenterUserLocation) {
      setMapCenter(user?.location ?? defaultMapCenter);
    } else {
      setMapCenter(officeLocation ? officeLocation : defaultMapCenter);
    }
  }, [
    mapCenterUserLocation,
    setMapCenter,
    user?.location,
    salesOfficeOptions,
    sectionOptions,
    workplaceFilterParameters,
    setUserOfficeLocation,
  ]);

  useEffect(() => {
    if (!workplaceFilterParameters.branchCode) {
      setSalesOfficeOptions([]);
      setSectionOptions([]);
      return;
    }
    fetchSalesOfficeOptions(workplaceFilterParameters.branchCode);
  }, [
    workplaceFilterParameters.branchCode,
    fetchSalesOfficeOptions,
    setSalesOfficeOptions,
    setSectionOptions,
  ]);

  useEffect(() => {
    if (!workplaceFilterParameters.branchCode || !workplaceFilterParameters.salesOfficeCode) {
      setSectionOptions([]);
      return;
    }
    fetchSectionOptions(
      workplaceFilterParameters.branchCode,
      workplaceFilterParameters.salesOfficeCode
    );
  }, [
    workplaceFilterParameters.branchCode,
    workplaceFilterParameters.salesOfficeCode,
    fetchSectionOptions,
    setSectionOptions,
  ]);

  const handleBranchChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBranchCode = event.target.value;
    if (selectedBranchCode) setShowMissingBranchOffice(false);

    patchWorkplaceFilterParameters({
      branchCode: selectedBranchCode,
      salesOfficeCode: '',
      sectionCode: '',
      squadCode: '',
    });
  };

  const handleSalesOfficeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSalesOfficeCode = event.target.value;
    if (selectedSalesOfficeCode) setShowMissingLocalOffice(false);

    patchWorkplaceFilterParameters({
      salesOfficeCode: selectedSalesOfficeCode,
      sectionCode: '',
      squadCode: '',
    });
  };

  const handleSectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    patchWorkplaceFilterParameters({ sectionCode: event.target.value, squadCode: '' });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSafeRows = (data: any[] | undefined | null) => {
    if (data && data.length > 0) {
      return data;
    }
    return [{ squadCode: '', squadSummary: 0 }];
  };

  const generateData = () => {
    const products = getSafeRows(summary?.numOfProducts);
    const suspended = getSafeRows(summary?.numOfSuspendedProducts);
    const unknown = getSafeRows(summary?.numOfStatusUnknownProducts);
    const handled = getSafeRows(summary?.numOfHandledProducts);
    const entrapped = getSafeRows(summary?.numOfProductsWithEntrapmentIssues);

    return [
      {
        headerLabel: t('wards.common.squad'),
        headerValue: t('wards.progressCheck.productsNumber'),
        rows: products,
      },
      {
        headerLabel: t('wards.common.squad'),
        headerValue: t('wards.progressCheck.suspendedProductsNumber'),
        rows: suspended,
      },
      {
        headerLabel: t('wards.common.squad'),
        headerValue: t('wards.progressCheck.unknownProducts'),
        rows: unknown,
      },
      {
        headerLabel: t('wards.common.squad'),
        headerValue: t('wards.progressCheck.handledProductsNumber'),
        rows: handled,
      },
      {
        headerLabel: t('wards.common.squad'),
        headerValue: t('wards.progressCheck.entrappedProductsNumber'),
        rows: entrapped,
      },
    ];
  };

  const handleSearchAction = () => {
    const { branchCode, salesOfficeCode, sectionCode } = workplaceFilterParameters;

    const isBranchMissing = !branchCode;
    const isSalesOfficeCodeMissing = !salesOfficeCode;
    const isSectionMissing = !sectionCode;

    setShowMissingBranchOffice(isBranchMissing);
    setShowMissingLocalOffice(isSalesOfficeCodeMissing);
    setShowMissingSection(isSectionMissing);

    if (isBranchMissing || isSalesOfficeCodeMissing || isSectionMissing) return;

    fetchWorkplaceMobileSummary({
      branchCode: branchCode,
      salesOfficeCode: salesOfficeCode,
      sectionCode: sectionCode,
    });
  };

  const handleMenuAction = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.RecoverySupportMenu].path);
  };

  const branchDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...branchOptions.map((b) => ({ title: b.code + ' ' + b.name, value: b.code })),
  ];
  const salesOfficeDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...salesOfficeOptions.map((o) => ({ title: o.code + ' ' + o.name, value: o.code })),
  ];
  const sectionDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...sectionOptions.map((s) => ({ title: s.code + ' ' + s.name, value: s.code })),
  ];

  const selectedBranchOption = branchDropdownOptions.find(
    (opt) => opt.value === workplaceFilterParameters.branchCode
  );
  const selectedSalesOfficeOption = salesOfficeDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.salesOfficeCode
  );
  const selectedSectionOption = sectionDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.sectionCode
  );

  return (
    <div className="flex min-h-screen flex-col space-y-4 bg-gray-100 px-4 py-8">
      <h2 className="mb-4 text-left text-2xl font-bold">{t('wards.progressCheck.title')}</h2>
      <div className="space-y-4">
        <FormRow
          title={t('wards.common.branchOffice')}
          layout="vertical"
          showError={showMissingBranchOffice}
          errorMessage={t('wards.progressCheck.missingBranchOffice')}
          required
        >
          <DropdownSelect
            selectedValue={selectedBranchOption ?? { title: t('wards.common.none'), value: '' }}
            optionValues={branchDropdownOptions}
            onChange={handleBranchChange}
          />
        </FormRow>

        <FormRow
          title={t('wards.common.salesOffice')}
          layout="vertical"
          showError={showMissingLocalOffice}
          errorMessage={t('wards.progressCheck.missingSalesOffice')}
          required
        >
          <DropdownSelect
            selectedValue={
              selectedSalesOfficeOption ?? { title: t('wards.common.none'), value: '' }
            }
            optionValues={salesOfficeDropdownOptions}
            onChange={handleSalesOfficeChange}
          />
        </FormRow>

        <FormRow
          title={t('wards.common.section')}
          layout="vertical"
          required
          showError={showMissingSection}
          errorMessage={t('wards.progressCheck.missingSection')}
        >
          <DropdownSelect
            selectedValue={selectedSectionOption ?? { title: t('wards.common.none'), value: '' }}
            optionValues={sectionDropdownOptions}
            onChange={handleSectionChange}
          />
        </FormRow>
      </div>
      <div className="grid grid-cols-2 gap-4 pt-4">
        <Button title={t('wards.progressCheck.search')} onClick={handleSearchAction} />
        <Button title={t('wards.progressCheck.menu')} onClick={handleMenuAction} />
      </div>
      {summary && (
        <div className="bg-gray-100 p-2">
          {generateData().map((cell, idx) => (
            <ProgressSearchCell
              key={idx}
              headerLabel={cell.headerLabel}
              headerValue={cell.headerValue}
              rows={cell.rows}
            />
          ))}
        </div>
      )}
    </div>
  );
}
